<template>
  <div class="padded">

    <edit-pane v-if="!hidePanel" ref="cd" :title="form.name"
    :is-edit="editModeState" :prefix="prefix" :show-edit-button="isAdding || profile().login === form.login || isSuper() || isManager('admin|management')"
    :parent-managed="parentManaged"
    :self-editable="!isAdding && profile().login === form.login"
    @save="onSave" @cancel="onCancel" @edit="onEdit" @add="onAdd"
    :show-background="!isAdding"  />

    <el-form ref="contactDetailsForm" label-width="90px">

      <el-tabs type="border-card"
        :active-name="currentTab"
        :before-leave="canLeaveTab"
        @tab-click="tabChanged">
        <el-tab-pane name="contact" label="Contact">

          <el-row>
            <el-form-item label="Name" required>
              <el-input name="name" required v-model="form.name" :disabled="disabled" placeholder="Full Name" />
            </el-form-item>

            <el-form-item label="Short Name">
              <el-input name="shortName" v-model="form.shortName" :disabled="disabled" placeholder="Short Name" />
            </el-form-item>

            <el-form-item label="Job Title">
              <el-input name="jobTitle" v-model="form.jobTitle" :disabled="disabled" />
            </el-form-item>

          </el-row>

          <el-row>
            <el-form-item label="Phone">
              <el-input type="phone" name="phone" v-model="form.phone" :disabled="disabled" />
            </el-form-item>
            <el-form-item label="E-mail">
              <el-input type="email" name="email" v-model="form.email" :disabled="disabled" />
            </el-form-item>
          </el-row>

          <el-row>
            <el-col :span="12">
              <check-box label="Upload Alerts" v-model="form.sendUploadAlerts" :disabled="disabled" />
            </el-col>

            <el-col :span="12">
              <check-box label="Approval Alerts" v-model="form.sendApprovalAlerts" :disabled="disabled" />
            </el-col>
          </el-row>

          <!-- XOL-392: on /profile the password control lives HERE, on the one
               tab that screen has. Everywhere else it sits on System Details
               beside the Login ID it belongs to. "My account" is not a
               contact-admin screen: there is no Enabled?, no Login ID field and
               no privileges to sit next to, so a tab for one button would be
               chrome for its own sake. Always shown — /profile is by definition
               the self case, which PUT /password/change/{id} permits
               (ChangePasswordRequest::authorize() is self-or-super) and which
               is exempt from the view-only write block (XOL-371). -->
          <div v-if="selfProfile" class="text-right">
            <password-dialog v-model="form" change :disabled="false" />
          </div>

        </el-tab-pane>



        <!-- XOL-392: this tab is the account surface — Enabled?, Login ID and
             the password all live here now, in both create and edit. It is an
             administrator acting ON a contact, so it is suppressed entirely on
             /profile (selfProfile), which has its own password button above.
             Credentials are offered on create only for a super — see
             canStageCredential — because stripSuperOnlyFields() keeps
             `password` while stripping `login`/`enabled` for anyone else. -->
        <el-tab-pane name="systemDetails" v-if="showSystemDetails" label="System Details">

          <!-- XOL-392 (2026-08-20): three blocks, per Carlos.
                 LEFT   - who this account is: Enabled?, Login ID, password.
                 RIGHT  - what it can see: Default View + the roles transfer.
                 BOTTOM - what it may do: the four privilege toggles, full width
                          because each carries an explanatory alert and four of
                          those do not fit in half a row.
               Left and right go full width below `lg`: el-transfer is two
               panels plus a button column and overflows a half column at md. -->
          <el-row :gutter="12">

            <!-- CREATE ONLY: full width above both columns. -->
            <el-col v-if="canStageCredential" :span="24">
              <el-alert
                type="info" show-icon :closable="false"
                title="Sign-in details (optional)"
                description="Fill these in to let this contact sign in. They are saved together with the contact — nothing is e-mailed, so pass the Login ID and password on yourself. Leave them blank for a contact who does not need to sign in." />
            </el-col>

            <!-- ───────────────────────── BLOCK 1 — LEFT ───────────────────────── -->
            <el-col :xs="24" :sm="24" :md="24" :lg="12">

              <el-form label-position="top" :inline="true">
                <el-row>
                <el-col :span="24">
                  <switch-input v-if="canEditSystemFields"
                    label="Enabled?"
                    on-text="Enabled"
                    off-text="Disabled"
                    v-model="form.enabled"
                    :disabled="disabled" />

                  <text-input
                  label="Login ID"
                   v-model="form.login"
                   placeholder="Login ID"
                  :disabled="disabled || !canEditSystemFields" />

                  <!-- Non-super on their own record: the field is read-only, so
                       name it. -->
                  <div v-if="!canEditSystemFields">
                    <small class="text-muted">This is the username you sign in with.</small>
                  </div>

                  <!-- EDIT ONLY: independent write to PUT /password/change/{id}.
                       Block-level div, so it drops to its own line under the
                       two inline-block form items above. Rendered without
                       requiring record edit mode — XOL-371 self-rotation, and
                       for a super the record's edit state is irrelevant to a
                       different endpoint's authz. -->
                  <div v-if="canManagePassword">
                    <password-dialog v-model="form" change :disabled="false" />
                    <div><small class="text-muted">Password changes save immediately and separately from this record.</small></div>
                  </div>

                  <!-- EDIT ONLY, super, contact has no login yet: name the
                       absence. -->
                  <div v-if="canEditSystemFields && !isAdding && !hasLoginAccount">
                    <small class="text-muted">Give this contact a Login ID and save the record — you can then set a password here.</small>
                  </div>

                </el-col>
              </el-row>
              </el-form>

              <!-- CREATE ONLY. Deliberately a SIBLING of the el-form--inline
                   above, not a descendant (FLAG 2): that form renders
                   class="el-form--inline", and .el-form--inline .el-form-item is
                   a descendant selector that would otherwise reach through
                   PasswordForm's own :inline="false" and force its two fields
                   into mismatched inline-blocks with right-aligned labels (the
                   XOL-451 gotcha). A plain div, because its parent is already
                   the column. -->
              <div v-if="canStageCredential">

                <el-alert v-if="form.login && !form.enabled"
                  type="warning" show-icon :closable="false"
                  title="Account not enabled"
                  description="This contact will not be able to sign in until Enabled? is switched on. You can leave it off now and enable the account later." />

                <password-form v-model="form" :inline="false" />

                <!-- id matches the aria-describedby PasswordForm puts on both
                     inputs. Without it that reference dangles on the ONE surface
                     where all four credential rules are actually reachable. No
                     collision risk: canStageCredential and canManagePassword are
                     mutually exclusive, so this and the dialog's alert of the
                     same id never render together. -->
                <el-alert v-if="credentialError"
                  id="password-rules"
                  type="error" show-icon :closable="false"
                  :title="credentialError" />

              </div>

            </el-col>

            <!-- ───────────────────────── BLOCK 2 — RIGHT ────────────────────────
                 XOL-392: renders in create AND edit. `contactOrgCategory` falls
                 back to the org the contact is being created into, because the
                 contacts struct carries no orgCategory of its own; before that
                 fallback these fields silently never appeared on the create
                 form. The canEditSystemFields term inside showOrgRoleFields is
                 load-bearing twice over: every field here is in the API's
                 SUPER_ONLY_FIELDS, and on /profile `form` IS the live Vuex
                 profile() object, so without it a non-super could flip their own
                 Super / View Only switches and change the SPA's own gating for
                 the running session.
                 Each block keeps its own el-form wrapper — every child is an
                 el-form-item that reads labelPosition from an injected el-form
                 ancestor. -->
            <el-col v-if="showOrgRoleFields" :xs="24" :sm="24" :md="24" :lg="12">
              <el-form label-position="top" :inline="true">

                <select-input :list="roles" v-model="form.defaultView" label="Default View"  :disabled="disabled" />

                <el-transfer
                    v-if="!disabled"
                    v-model="formUserRoles"
                    :titles="['All Roles', 'User Roles']"
                    :data="roles"
                    @change="changeUserRoles" />

                <el-form-item  v-else label="User Roles">
                  <span  v-for="(role, i) in roles" :key="i">
                    <el-tag v-if="formUserRoles.includes(role.value)" :type="role.type">
                      {{role.label}}
                    </el-tag>&nbsp;
                  </span>
                </el-form-item>

              </el-form>
            </el-col>

            <!-- ──────────────────────── BLOCK 3 — BOTTOM ──────────────────────── -->
            <el-col v-if="showOrgRoleFields" :span="24">
              <el-form label-position="top" :inline="true">
                <el-row :gutter="5.28">

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="View Only" v-model="form.viewer" :disabled="disabled" />

                    <el-alert
                      title="View Only"
                      type="info"
                      description="View Only can't add, edit or delete anything. Only can create tickets."
                      show-icon
                      :closable="false" />
                  </el-col>

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Approver" v-model="form.approver" :disabled="disabled" />

                    <el-alert
                       title="Requests Module Only"
                       type="success"
                       description="Approver for Requests (can approve and send approval email)"
                       show-icon
                       :closable="false" />

                  </el-col>

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Manager" v-model="form.manager" :disabled="disabled" />

                    <el-alert
                      title="Manager"
                      type="warning"
                      description="Manager can add, edit and delete according to role(s)"
                      show-icon
                      :closable="false" />
                  </el-col>

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Super" v-model="form.super" :disabled="disabled" />

                    <el-alert
                      title="Super User"
                      type="error"
                      description="Super User can create users and delete anything even sites."
                      show-icon
                      :closable="false" />
                  </el-col>

                </el-row>
              </el-form>
            </el-col>

          </el-row>

      </el-tab-pane>

      <!-- XOL-392: Remarks are an administrator's notes ABOUT a contact, so
           they are suppressed on /profile. Leaving them there invited a user to
           annotate their own record in a field their managers read. -->
      <el-tab-pane name="remarks" v-if="!selfProfile && (isManager() || isSuper())" label="Remarks">

          <html-input v-model="form.remarks"
            label="Remarks"
            :show-empty-message="true"
            :disabled="disabled" />

      </el-tab-pane>

    </el-tabs>
  </el-form>

  <small v-if="!isAdding" class="text-muted">ID <b>{{form.organisation_id + '-' + form.id + '-' + form.created_at}}</b></small>
</div>

</template>


<script>

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'
import PasswordDialog from '@/components/login/PasswordDialog'
import PasswordForm from '@/components/login/PasswordForm'

export default {

  mixins: [SiteMixins],


  components: {
    PasswordDialog,
    PasswordForm
  },



  props: {
    rowData: {
      type: Object,
      required: true
    },

    prefix: {
      type: String,
      default: 'Contact'
    },

    // XOL-392: /profile reuses this component but is NOT a contact-admin
    // screen — it is "my account". It must not offer System Details (Enabled?,
    // Login ID, privileges) or Remarks, which are things an administrator does
    // TO a contact, not things a user does to themselves. Set by UserProfile
    // only. An explicit prop rather than sniffing `prefix`, which is display
    // copy and would silently re-enable the tabs if the wording ever changed.
    selfProfile: {
      type: Boolean,
      default: false
    },

    orgId: {
      type: Number
    },

    // XOL-392: the org category to assume while ADDING, when the form itself
    // cannot supply one. `getTableStruct('contacts')` has no `orgCategory` key
    // (it is an Eloquent $appends accessor, not a column), so on create
    // `form.orgCategory` is undefined and every org-gated System Details field
    // silently failed its v-if. Ignored on an existing record, where
    // `form.orgCategory` is authoritative — see `contactOrgCategory`.
    orgCategory: {
      type: String,
      default: null
    },

    isDisabled: {
      type: Boolean,
      default: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    tableRef: {
      type: String,
      default: 'contacts-table'
    },

    adding: {
      type: Boolean,
      default: false
    },

    hidePanel: {
      type: Boolean,
      default: false
    },

    parentManaged: {
      type: Boolean,
      default: true
    }

  },



  computed: {

    // XOL-392: own record. The !isAdding guard AND the truthy login check
    // both matter: on a fresh form both profile().login and form.login could
    // compare equal via null === null. Mirrors ProfileController (authUser =
    // jwt sub).
    isSelfRecord () {
      return !this.isAdding && !!this.form && !!this.form.login &&
        this.profile().login === this.form.login
    },

    // XOL-392: mirrors ContactsController::isSuperTowerCo(). isSuper()
    // already short-circuits on !isTowerCo() (AccessMixins), but both terms
    // are kept so the mapping to the API predicate stays legible.
    canEditSystemFields () {
      return this.isSuper() && this.isTowerCo()
    },

    // XOL-392: one source of truth for "which org category is this contact in".
    // The saved record wins when it has one; on create it cannot (no such key
    // in the contacts struct), so fall back to the category passed down from
    // the org whose contacts table launched the form. Reported by Carlos:
    // "Creation does not have all system details fields ... make sure they are
    // org type gated as in update."
    contactOrgCategory () {
      if (this.form && this.form.orgCategory) {
        return this.form.orgCategory
      }

      if (!this.isAdding || !this.orgCategory) {
        return null
      }

      // The ORG row's `category` and the CONTACT's `orgCategory` are not the
      // same string. Organisation.category carries a Xolas-Client marker —
      // e.g. Xolas Sdn Bhd is literally 'Tower Company Xolas-Client' — and the
      // API strips it in Contacts::getOrgCategoryAttribute() via
      // Organisation::removeXolasClientCategory(). Comparing the raw org value
      // to 'Tower Company' would therefore be false for precisely the orgs
      // this feature is for. Mirror the server's normalisation.
      return this.orgCategory.replace('Xolas-Client', '').replace(',', '').trim()
    },

    // XOL-392: the org-gated block inside System Details — Default View, the
    // role transfer and the four privilege switches. Tower-Company-only, and
    // super-only: every field in it is in the API's SUPER_ONLY_FIELDS, so a
    // non-super would watch the server discard the lot behind a 200. The
    // canEditSystemFields term also stops the block reaching a non-super on
    // /profile, where `form` IS the live Vuex profile() object and toggling
    // Super/View Only would change the SPA's own gating for the session.
    showOrgRoleFields () {
      return this.canEditSystemFields && this.contactOrgCategory === 'Tower Company'
    },

    // XOL-392: the ONE branch on which stripSuperOnlyFields() is a
    // pass-through, so login/enabled survive alongside the password and the
    // created account can actually sign in.
    //
    // The extra `profile().super` term is NOT redundant with isSuper().
    // AccessMixins.isSuper() returns `super || userRole === 'super'` — the
    // second arm dates to 8196acc6 (2022-04-29) and predates the 2026 work.
    // The API's ContactsController::isSuperTowerCo() checks `super` ONLY, so a
    // Tower Company contact with `super = false, userRole = 'super'` passes
    // here and fails there. That divergence is exactly the case this guard
    // exists to stop: SUPER_ONLY_FIELDS omits `password`, so store() would
    // strip login/enabled and still hash the password, writing a credential
    // nobody can use behind a 200. Not reachable through the UI today (the
    // roles select offers no 'super' entry) but reachable from legacy rows,
    // so the write path is pinned to the server's own predicate.
    canStageCredential () {
      return this.isAdding && this.canEditSystemFields && !!this.profile().super
    },

    // XOL-392: the account-administration surface — never on /profile, which
    // is "my account" and carries its own password button on the Contact tab.
    //
    // An earlier revision of this plan added an `|| isSelfRecord` arm so a
    // non-super would not lose self-service password change when the password
    // moved onto this tab. Giving /profile its own button removes that need
    // entirely, so the arm is gone: this is back to a plain administrator gate.
    // That also closes the privilege-leak risk it created — on /profile `form`
    // IS the live Vuex profile() object, so rendering the privileges section
    // there let a non-super flip their own `super`/`viewer` flags in-session.
    showSystemDetails () {
      return !this.selfProfile && this.canEditSystemFields
    },

    // XOL-391/XOL-392: there must be an account for a password to attach to
    // — a login id present. `enabled` is no longer required: PUT
    // /password/change/{id} does not care about it, and requiring it forced
    // a super to enable -> save -> then set the password. The switch now
    // sits in the same viewport as the password control, so the state is
    // visible where the decision is made.
    hasLoginAccount () {
      return !!(this.form && this.form.login)
    },

    // XOL-392: mirrors ChangePasswordRequest::authorize() — self-or-super.
    canManagePassword () {
      return !this.isAdding && this.hasLoginAccount &&
        (this.isSuper() || this.isSelfRecord)
    },

    // XOL-392: one source of truth for the four create-time credential
    // rules — the inline el-alert renders it live, validateCredential()
    // blocks the save with the same string. Rules mirror Register.vue and
    // ChangePasswordRequest's min:8. Order matters — first failure wins.
    credentialError () {
      if (!this.canStageCredential || !this.form || !this.form.password) {
        return null
      }
      if (!this.form.login) {
        return 'Enter a Login ID above before setting a password, or clear the password.'
      }
      if (this.form.password.length < 8) {
        return 'The password must be at least 8 characters.'
      }
      if (this.form.password !== this.form.c_password) {
        return 'The passwords don\'t match.'
      }
      if (this.form.password.indexOf(this.form.login) !== -1) {
        return 'The password must not contain the Login ID.'
      }
      return null
    }

  },


  methods: {

    changeUserRoles (roles) {
      let userRole = ''

      if (roles.length > 0) {

         for (let s in roles) {
             userRole += (s > 0 ? '|' + roles[s] : roles[s])
           }

         } else {
           userRole = this.formUserRoles[0]
         }

     this.form.userRole = userRole
    },



    resetData () {
      this.form.id = null
      this.form.name = null
      this.form.shortName = null
      this.form.jobTitle = null
      this.form.phone = null
      this.form.email = null
      this.form.enabled = false
      this.form.login = null
      this.form.sendUploadAlerts = false
      this.form.sendApprovalAlerts = false
      this.form.remarks = null

      // XOL-392: the org-gated role fields now RENDER on create, so they have
      // to be reset like everything else. The drawer is never destroyed, so
      // without this a super who ticked Super/Manager on one contact and
      // cancelled would silently carry those privileges into the next New
      // Contact — the worst possible field to leave sticky.
      this.form.defaultView = null
      this.form.userRole = null
      this.form.viewer = false
      this.form.approver = false
      this.form.manager = false
      this.form.super = false
      this.formUserRoles = []

      // XOL-392: the drawer is not destroyed on close — Popup.closeModal()
      // keeps the slot mounted — so a plaintext password typed here would
      // otherwise survive into the next New Contact. Assign field-by-field;
      // do NOT replace this.form wholesale (the struct has no c_password, so
      // swapping the object would hand PasswordForm a non-reactive property —
      // the exact bug XOL-391 fixed with $set).
      this.form.password = null
      this.form.c_password = null
    },



    success (response) {
      // XOL-392: the drawer is not destroyed on save either — Popup.saveModal()
      // emits 'save' but never 'close' and el-drawer keeps the slot mounted —
      // so a plaintext password typed here would otherwise survive into the
      // next New Contact. ContactAction builds contactData once in data() and
      // reuses the same object, so this is the only clear-point on the save
      // path.
      if (this.isAdding) {
        this.form.password = null
        this.form.c_password = null
      }

      this.initialForm = { ...this.form }
      this.$events.emit(this.tableRef + '-refresh-table')
    },



    onCancel () {
      this.disabled = true
      if (this.parentManaged) this.editModeState = false

      // Blur the active element if it belongs to this form before resetting.
      // Rationale: after a blocked tab-switch, the browser can latch an
      // invalid required <input> into a visual state where a subsequent
      // programmatic value assignment (via Element UI's nativeInputValue
      // watcher) is not repainted. Releasing focus clears the latch.
      // See .context/plans/contact-detail-stale-input-plan.md.
      const formEl = this.$refs.contactDetailsForm && this.$refs.contactDetailsForm.$el
      const active = typeof document !== 'undefined' ? document.activeElement : null
      if (formEl && active && formEl.contains(active) && typeof active.blur === 'function') {
        active.blur()
      }

      this.$nextTick(() => {
        this.form = { ...this.initialForm }
      })
    },



    onEdit () {
      this.disabled = false
      if (this.parentManaged) this.editModeState = true
    },



    onAdd () {
      this.disabled = false
      this.isAdding = true

      var id = this.form.organisation_id
      this.form = this.getTableStruct('contacts')
      this.form.organisation_id = id
      if (this.parentManaged) this.editModeState = true
    },


    // Bound to el-tabs :before-leave — the original validateForm() body,
    // verbatim. Returning false here BLOCKS the tab switch (Element's
    // tabs.vue), so it must never contain a rule the user can only fix on
    // another tab — see validateCredential() below.
    canLeaveTab () {
      const form = this.$refs.contactDetailsForm.$el
      if (!form.checkValidity()) {
        form.reportValidity()
        return false
      }
      return true
    },

    validateForm () {
      return this.canLeaveTab() && this.validateCredential()
    },

    // XOL-392: the inline create-time password fields sit inside the outer
    // <el-form>, but pairing and length rules are not expressible natively,
    // and reportValidity() cannot focus a control on a v-show-hidden tab
    // pane (the Contact tab can be the active one when Save is clicked).
    // Rules mirror Register.vue and credentialError above — kept as a single
    // computed so the live alert and this blocking check can never drift.
    validateCredential () {
      var problem = this.credentialError
      if (!problem) {
        return true
      }
      this.$message({ type: 'error', message: problem })
      return false
    },

    // XOL-392: fires ONLY when the /contacts save 422s with a login-specific
    // validation error. The generic PostMixins.send() message already names
    // the DB column ("The login has already been taken.") without saying
    // which tab to fix it on — this adds the one extra, targeted message.
    // API item A5 (ContactsRequest::attributes()) retires this once it lands.
    onDuplicateLogin (errors) {
      if (errors && errors.login) {
        this.$message({
          type: 'error',
          message: 'That Login ID is already taken. Choose a different one on the System Details tab.'
        })
      }
    },

    onSave (orgId) {
      if (!this.validateForm()) {
        if (this.parentManaged) this.editModeState = true
        return
      }

      if (orgId && this.isAdding) {
        this.org_id = orgId
        this.form.organisation_id = this.org_id
      }

      if (this.form.sendUploadAlerts || this.form.sendApprovalAlerts) {

        if (this.isNull(this.form.sendUploadAlerts)) {
          this.form.sendUploadAlerts = false
        }

        if (this.isNull(this.form.sendApprovalAlerts)) {
          this.form.sendApprovalAlerts = false
        }

      }

      this.disabled = this.isAdding ? false : true
      if (this.parentManaged) this.editModeState = false

      // XOL-371: a contact editing their OWN record saves to the self-service
      // profile endpoint (server derives the user from the JWT, allow-list
      // fields only). Managing another contact, or adding one, keeps the
      // existing /contacts resource save.
      if (!this.isAdding && this.profile().login === this.form.login) {
        this.saveSelfProfile()
      } else {
        this.saveForm(this, '/contacts', this.form.id, this.contactPayload(), this.form.name, this.success, this.onDuplicateLogin)
      }
    },


    // XOL-391: the API validates `login` with `sometimes|unique:contacts,login`.
    // `sometimes` only skips a key that is ABSENT — sending `login: null` still
    // runs the unique check, and Laravel turns a null value into
    // `WHERE login IS NULL`, which matches every login-less contact already in
    // the table. Creating a contact without a login therefore failed with a
    // bogus "The login has already been taken." (resetData() sets login to
    // null, so this hit every new contact that wasn't given a login id).
    // Omit the key entirely when there is no login — exactly what `sometimes`
    // is for — so the column keeps its NULL default instead of an empty string.
    // Only the create path validates login this way (update() takes a plain
    // Request, not ContactsRequest), but the payload is built the same way for
    // both so an empty login never means "clear this field to ''".
    contactPayload () {
      var payload = { ...this.form }

      if (payload.login === null || payload.login === undefined || payload.login === '') {
        delete payload.login
      }

      // XOL-392: `password` travels with POST /contacts ONLY when a super is
      // creating. On every other path the /contacts resource carries no
      // credential: PUT /contacts/{id} would hash it (ContactsController
      // update()) with no min:8 rule and a WIDER authz than /password/change
      // (isSameOrgManager || isSelf), and for a non-super caller store()
      // strips login/enabled while KEEPING password — an orphan credential on
      // a contact that cannot sign in. normalizePassword() also drops
      // empty/null values server-side, so the !payload.password arm is
      // belt-and-braces.
      if (!this.canStageCredential || !payload.password) {
        delete payload.password
      }

      // `c_password` is the confirm-field companion the password components
      // seed onto the shared form — not a contacts column, never sendable.
      delete payload.c_password

      return payload
    },


    // XOL-371: PUT /api/profile — self-service save of the logged-in
    // contact's own record. Own record only (server: contact.id === jwt.sub);
    // send ONLY the self-editable fields (no role/system fields). Mirrors
    // saveForm()'s use of PostMixins' send() for success/error handling.
    // Paired endpoint: ProfileController@update in xolas-tms-api (XOL-371).
    //
    // XOL-392: `password` is deliberately NOT sent here, even though it is in
    // ProfileController::SELF_EDITABLE server-side — this is an SPA-side
    // decision, not a server constraint. No UI intends to set a password via
    // PUT /api/profile: the password dialog performs its own, independent
    // PUT /password/change/{id}. On /profile `form` IS the live Vuex
    // profile() object (assigned by reference), and PasswordDialog's
    // formChange() replaces its OWN form reference on fetch — so it does not
    // reliably clear ContactDetail's copy. Not sending it here is the only
    // reliable fix for a cancelled/dismissed dialog password otherwise riding
    // this save.
    saveSelfProfile () {
      var payload = {
        name: this.form.name,
        shortName: this.form.shortName,
        jobTitle: this.form.jobTitle,
        phone: this.form.phone,
        email: this.form.email,
        sendUploadAlerts: this.form.sendUploadAlerts,
        sendApprovalAlerts: this.form.sendApprovalAlerts
      }

      // allowViewOnly=true (last arg): /profile is the one self-service surface a
      // view-only user may write; the API keeps PUT /api/profile exempt too.
      this.send(this, this.getEndpoint('profile', null, false), payload, this.form.name, this.success, null, axios.put, true, 'Save', null, false, true)
    },


    tabChanged (pane) {
      this.currentTab = pane.name
    },



    onClose () {
      this.resetData()
    }

  },



  data () {
    const form = this.adding ? this.getTableStruct('contacts') : this.rowData

    let formUserRoles = []
    if (form.userRole) {
      formUserRoles = form.userRole.trim().split('|')
    }

    return {
      disabled: this.isBoolean(this.isDisabled) ? this.isDisabled : true,
      emailDisabled: !(this.adding && this.isManager() || this.profile().login === this.rowData.login),
      isAdding: this.adding,
      formUserRoles: formUserRoles,
      roles: [
       { key: 'project', label: 'Project', value: 'project', type: 'primary' },
       { key: 'property', label: 'Property', value: 'property', type: 'secondary' },
       { key: 'maintenance', label: 'Maintenance', value: 'maintenance', type: 'warning' },
       { key: 'finance', label: 'Finance', value: 'finance', type: 'success' },
       { key: 'admin', label: 'Admin', value: 'admin', type: 'info' },
       { key: 'management', label: 'Management', value: 'management', type: 'primary' }
      ],
      org_id: (this.rowData.organisation_id ? this.rowData.organisation_id : this.orgId),
      form: form,
      initialForm: { ...form },
      editModeState: this.editMode,
      currentTab: 'contact'
    }
  },

  created () {

    if (this.isAdding) {
      this.disabled = false
      delete this.form.id
    }

    if (!this.form.organisation_id) {
      this.form.organisation_id = this.org_id
    }

  }

}

</script>
